import React, { useEffect, useRef, useState } from 'react';
import { pinDeleteError } from '../../lib/admin-pin-client';
import { PinDialog } from './PinDialog';

export interface PinDeleteRequest {
  title: string;
  description: string;
  confirmLabel?: string;
  busyLabel?: string;
  run: (pin: string) => Promise<void>;
}

interface PinDeleteDialogProps {
  title: string;
  description: string;
  confirmLabel: string;
  busyLabel: string;
  onConfirm: (pin: string) => Promise<void>;
  onClose: () => void;
}

/** PIN prompt for one delete. Errors stay in the dialog. A wrong PIN does not close it. */
export function PinDeleteDialog({
  title,
  description,
  confirmLabel,
  busyLabel,
  onConfirm,
  onClose,
}: PinDeleteDialogProps) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const busyRef = useRef(false);

  useEffect(() => {
    if (lockedUntil == null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [lockedUntil]);

  const lockSeconds = lockedUntil == null ? 0 : Math.max(0, Math.ceil((lockedUntil - now) / 1000));
  const shownError = lockSeconds > 0 ? `Too many wrong PINs. Try again in ${lockSeconds} seconds.` : error;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busyRef.current || lockSeconds > 0 || pin.length === 0) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(pin.trim());
      onClose();
    } catch (err) {
      const failure = pinDeleteError(err);
      setError(failure.message);
      if (failure.clearPin) setPin('');
      setLockedUntil(failure.lockSeconds != null ? Date.now() + failure.lockSeconds * 1000 : null);
      setNow(Date.now());
      busyRef.current = false;
      setBusy(false);
    }
  }

  return (
    <PinDialog
      titleId="admin-pin-delete-title"
      title={title}
      description={description}
      pin={pin}
      onPinChange={setPin}
      error={shownError}
      busy={busy}
      submitDisabled={lockSeconds > 0}
      confirmLabel={confirmLabel}
      busyLabel={busyLabel}
      onSubmit={(event) => void submit(event)}
      onClose={onClose}
    />
  );
}

/** One shared delete prompt for the page that owns the list. */
export function usePinDelete() {
  const [request, setRequest] = useState<PinDeleteRequest | null>(null);

  function ask(next: PinDeleteRequest) {
    setRequest(next);
  }

  const dialog = request ? (
    <PinDeleteDialog
      key={request.title}
      title={request.title}
      description={request.description}
      confirmLabel={request.confirmLabel ?? 'Delete'}
      busyLabel={request.busyLabel ?? 'Deleting…'}
      onConfirm={request.run}
      onClose={() => setRequest(null)}
    />
  ) : null;

  return { ask, dialog };
}
